import React, { useEffect, useState } from 'react'
import { formatCurrency } from '../../../../util/string'
import { BalanceReferenceKind, getPaymentAmountBalanceWarning } from './paymentAmountBalanceWarningUtil'

const WARNING_DEBOUNCE_MS = 500

function parseAmount(amount: string | number): number {
    return typeof amount === 'string' ? Number(amount) : amount
}

function shouldSyncAmountImmediately(amount: string | number): boolean {
    if (amount === '') {
        return true
    }

    const numericAmount = parseAmount(amount)
    return !Number.isFinite(numericAmount) || numericAmount <= 0
}

interface PaymentAmountBalanceWarningProps {
    amount: string | number
    balanceTotal: number
    balanceDue?: number
}

function formatBalanceReferences(balanceTotal: number, balanceDue: number | undefined, applicableReferences: BalanceReferenceKind[]): string {
    const references: string[] = []

    if (applicableReferences.includes('total') && Number.isFinite(balanceTotal) && balanceTotal >= 0) {
        references.push(`balance total of ${formatCurrency(balanceTotal)}`)
    }

    const due = balanceDue ?? 0
    if (applicableReferences.includes('due') && due > 0) {
        references.push(`balance due of ${formatCurrency(due)}`)
    }

    if (references.length <= 1) {
        return references[0] ?? ''
    }

    return `${references[0]} and ${references[1]}`
}

const PaymentAmountBalanceWarning: React.FC<PaymentAmountBalanceWarningProps> = ({ amount, balanceTotal, balanceDue }) => {
    const [debouncedAmount, setDebouncedAmount] = useState(amount)

    useEffect(() => {
        if (shouldSyncAmountImmediately(amount)) {
            setDebouncedAmount(amount)
            return
        }

        const timer = setTimeout(() => setDebouncedAmount(amount), WARNING_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [amount])

    if (amount !== debouncedAmount) {
        return null
    }

    const warning = getPaymentAmountBalanceWarning(parseAmount(debouncedAmount), balanceTotal, balanceDue)

    if (warning == null) {
        return null
    }

    const referenceText = formatBalanceReferences(balanceTotal, balanceDue, warning.applicableReferences)
    const hasPositiveReference = balanceTotal > 0 || (balanceDue ?? 0) > 0
    const message =
        warning.type === 'over' && !hasPositiveReference
            ? 'Warning: This account has no outstanding balance'
            : warning.type === 'over'
              ? `Warning: This amount exceeds 120% of the account ${referenceText}`
              : `Warning: This amount is less than 80% of the account ${referenceText}`

    return (
        <div className="text-yellow-600 dark:text-yellow-400 text-sm mt-1 flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="inline mr-1 h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
            </svg>
            <span>{message}</span>
        </div>
    )
}

export default PaymentAmountBalanceWarning
